import { Role } from '@zorro/clients';
import { UserRoles } from '@zorro/types';
import { $enum } from 'ts-enum-util';

export const getRoleFronteggKey = (role: Role) =>
  $enum.mapValue(role).with({
    [Role.OMNIPOTENT_ADMIN]: 'OmnipotentAdmin',
    [Role.OPERATOR]: 'Operator',
    [Role.AGENCY_ADMIN]: 'AgencyAdmin',
    [Role.AGENT]: 'Agent',
    [Role.ACCOUNT_SUPERVISOR]: 'AccountSupervisor',
    [Role.EMPLOYER_ADMIN]: 'EmployerAdmin',
    [Role.EMPLOYEE]: 'Employee',
    [Role.USER]: 'User',
    [Role.ANY]: 'Any',
  });

export const FRONTEGG_ROLE_TO_ID_MAP: Record<string, Role> = {
  OmnipotentAdmin: Role.OMNIPOTENT_ADMIN,
  Operator: Role.OPERATOR,
  AgencyAdmin: Role.AGENCY_ADMIN,
  Agent: Role.AGENT,
  AccountSupervisor: Role.ACCOUNT_SUPERVISOR,
  EmployerAdmin: Role.EMPLOYER_ADMIN,
  Employee: Role.EMPLOYEE,
  User: Role.USER,
} as const;

export const mapSessionRolesToZorroRoles = (sessionRoles: string[]): Role[] => {
  return sessionRoles
    .map((role) => FRONTEGG_ROLE_TO_ID_MAP[role])
    .filter((role) => role !== undefined);
};

export const getRolesFromSession = (session: UserRoles): Role[] => {
  return mapSessionRolesToZorroRoles(session.roles);
};

export function doesUserSessionHaveRole(user: UserRoles, allowedRoles: Role[]) {
  const mappedRoles = mapSessionRolesToZorroRoles(user.roles);

  return mappedRoles.some((role) => allowedRoles.includes(role));
}

export const isAgentRole = (roles: string[]): boolean => {
  return (
    roles.includes(Role.AGENT) &&
    !roles.includes(Role.OMNIPOTENT_ADMIN) &&
    !roles.includes(Role.OPERATOR)
  );
};

export const isEmployeeOnlyRole = (roles: string[]): boolean => {
  return roles.includes(Role.EMPLOYEE) && roles.length === 1;
};

export const isAgentWithoutSupervisor = (roles: string[]): boolean => {
  const isAgent = isAgentRole(roles);
  return isAgent && !roles.includes(Role.ACCOUNT_SUPERVISOR);
};

export const isZorroOperationsRole = (roles: string[]): boolean => {
  return roles.includes(Role.OMNIPOTENT_ADMIN) || roles.includes(Role.OPERATOR);
};

export const isOmnipotentAdmin = (roles: string[]): boolean => {
  return roles.includes(Role.OMNIPOTENT_ADMIN);
};

export const isAgencyAdminRole = (roles: string[]): boolean => {
  return roles.includes(Role.AGENCY_ADMIN);
};

export const isAccountSupervisor = (roles: string[]): boolean => {
  return roles.includes(Role.ACCOUNT_SUPERVISOR);
};

export const isEmployerAdminRole = (roles: string[]): boolean => {
  return roles.includes(Role.EMPLOYER_ADMIN);
};

export const isManagement = (roles: string[]): boolean => {
  return (
    roles.includes(Role.OMNIPOTENT_ADMIN) ||
    roles.includes(Role.OPERATOR) ||
    roles.includes(Role.AGENT)
  );
};

export const isAgentOrAdminRole = (roles: string[]): boolean =>
  isManagement(roles) || isEmployerAdminRole(roles);
